import {
  APP_CONFIG,
  authTktState,
  canDebugStuffState,
  currentUserState,
  DisclosureResponse,
  getGuidFromHref,
  ROUTES
} from '@sd/shared';
import { useEffect, useState } from 'react';
import { Alert, Row } from 'react-bootstrap';
import { useRecoilValue } from 'recoil';
import { Loader } from '../Loader/Loader';
export type QNAAnnouncerSource = 'seller' | 'inspector' | 'experian';

export type QNAAnnouncementState = {
  code: string;
  source?: string;
  username?: string;
};

export type QNAInitialState = {
  announcements: QNAAnnouncementState[];
  questionResponses: DisclosureResponse[];
};

export type QNAConfig = {
  environment: 'integration1' | 'integration5' | 'uat' | 'production';
  rootElementId: string;
  authToken: string;
  clientApplicationName: string;
  unitHref?: string;
  onStateChange: (newState: QNAState) => void;
  announcerSource: Exclude<QNAAnnouncerSource, 'experian'>;
  username: string;
  questionnaireHref?: string;
  debug?: boolean;
  readonly?: boolean;
  initialState: QNAInitialState;
};

export type QNALibrary = {
  renderQuestions: (config: QNAConfig) => void;
  renderAnnouncements: (config: QNAConfig) => void;
};

export type QNAState = {
  valid: boolean;
  prompting: boolean;
  totalQuestions: number;
  questionnaireHref: string;
} & QNAInitialState;

const environment = (
  {
    dev: 'integration1',
    test: 'integration1',
    int1: 'integration1',
    int5: 'integration5',
    uat: 'uat',
    prod: 'production'
  } as const
)[APP_CONFIG.build.environment];

export type QNAProps = {
  scene: 'questions' | 'announcements';
  unitIdOrHref: string;
  initialState: QNAInitialState;
  questionnaireHref?: string;
  readonly?: boolean;
  onStateChange: (newState: QNAState) => void;
  announcerSource: QNAConfig['announcerSource'];
};
declare global {
  // eslint-disable-next-line no-var
  var QuestionsAndAnnouncements: QNALibrary | undefined;
}
if (!('QuestionsAndAnnouncements' in window)) {
  // eslint-disable-next-line functional/immutable-data
  window.QuestionsAndAnnouncements = undefined;
}

/**
 * Generic wrapper for the QNA Library:
 *
 * https://ghe.coxautoinc.com/SellerTools/questions-engine/blob/master/packages/questionsAndAnnouncementsUi/README.md
 *
 * NOTE: DO NOT put scene/module specific logic in this component. Instead, create a wrapper component in your
 * module that uses this to render the QNA libraray.
 */
export const QNA = (props: QNAProps) => {
  const [renderError, setRenderError] = useState<Error>();
  const [isQNALibraryLoaded, setIsQNALibraryLoaded] = useState(!!window.QuestionsAndAnnouncements);
  const [hasQNARendered, setHasQNARendered] = useState(false);
  const authTkt = useRecoilValue(authTktState);
  const currentUser = useRecoilValue(currentUserState);
  const canDebugStuff = useRecoilValue(canDebugStuffState);

  // Load the library onto the page
  useEffect(() => {
    if (!isQNALibraryLoaded) {
      const scriptTag = document.createElement('script');
      // eslint-disable-next-line functional/immutable-data
      scriptTag.src = APP_CONFIG.addVehicle.qnaLibraryUrl;
      document.head.appendChild(scriptTag);
      const timer = setInterval(() => {
        if (window.QuestionsAndAnnouncements) {
          clearInterval(timer);
          setIsQNALibraryLoaded(true);
        }
      }, 100);
      return () => clearInterval(timer);
    }
  }, []);

  // Render the QNA UI
  useEffect(() => {
    if (!window.QuestionsAndAnnouncements) return;
    if (!currentUser) return;
    setRenderError(undefined);
    if (
      APP_CONFIG.build.environment !== 'prod' &&
      props.scene === 'announcements' &&
      !props.readonly &&
      props.initialState.questionResponses.length &&
      !props.questionnaireHref
    ) {
      setRenderError(
        new Error(
          'DeveloperError: You have rendered the announcements with question responses but without a proper questionnaireHref. This is not a good idea. You should specify exactly which questionnaire these responses align with.'
        )
      );
    }
    setHasQNARendered(false);
    try {
      window.QuestionsAndAnnouncements[props.scene === 'announcements' ? 'renderAnnouncements' : 'renderQuestions']({
        debug: canDebugStuff,
        readonly: props.readonly,
        questionnaireHref: props.questionnaireHref,
        environment,
        rootElementId: 'qna-root',
        authToken: authTkt!,
        clientApplicationName: 'seller-dashboard-ui',
        unitHref: props.unitIdOrHref
          ? ROUTES.external.mashery.unit.externalUrlFor({ guid: getGuidFromHref(props.unitIdOrHref) })
          : undefined,
        onStateChange: newState => {
          setHasQNARendered(true);
          props.onStateChange(newState);
        },
        announcerSource: props.announcerSource,
        username: currentUser.username,
        initialState: props.initialState
      });
    } catch (e) {
      setHasQNARendered(true);
      const error = e instanceof Error ? e : undefined;
      setRenderError(error);
    }
  }, [isQNALibraryLoaded]);

  return (
    <Loader loading={!hasQNARendered}>
      {renderError && (
        <Row>
          <Alert variant="danger">{renderError.message}</Alert>
        </Row>
      )}
      <div id="qna-root" />
    </Loader>
  );
};
